import { COMPANY_PROFILE_LEGACY_SEGMENTS, LOCALIZED_PATHS } from '../constants/url-constants';

function safeDecodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Parses employer profile URLs into the Algolia company objectID segment.
 * Supports locale-prefixed paths (`/en/company/…`, `/fi/yritys/…`, `/sv/arbetsgivare/…`)
 * and logical `/company/…` (no locale prefix).
 */
export function extractCompanyProfileSlugFromUrlPathname(pathname: string): string | null {
  if (!pathname || pathname === '/') return null;
  const pathOnly = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  const parts = pathOnly.split('/').filter(Boolean);
  if (parts.length < 2) return null;

  // Logical: /company/:slug(+) (Vike's filesystem route name, distinct from the public
  // LOCALIZED_PATHS[locale].company segment)
  if (parts[0] === 'company') {
    const slug = parts
      .slice(1)
      .map((p) => safeDecodeSegment(p))
      .join('/');
    return slug.trim() !== '' ? slug : null;
  }

  // Localized: /:locale/:companySeg/:slug(+). Legacy segments (/fi/yritys/, /en/company/) still
  // match so the data loader can 301 them to the current form in a single hop.
  if (parts.length >= 3) {
    const loc = parts[0];
    if (loc !== 'en' && loc !== 'fi' && loc !== 'sv') return null;
    const accepted = [LOCALIZED_PATHS[loc].company, ...COMPANY_PROFILE_LEGACY_SEGMENTS[loc]];
    if (!accepted.includes(parts[1])) return null;
    const slug = parts
      .slice(2)
      .map((p) => safeDecodeSegment(p))
      .join('/');
    return slug.trim() !== '' ? slug : null;
  }

  return null;
}

function normalizeCompanySlugFromRouteParam(raw: string): string {
  // Match pathname parsing: each path segment is decoded separately so literal "%"
  // in a segment does not break decoding of the whole slug string.
  return raw
    .split('/')
    .map((p) => safeDecodeSegment(p))
    .join('/');
}

function routeParamCompanyslug(routeParams: Record<string, unknown> | undefined): string | null {
  if (!routeParams) return null;
  const raw = routeParams.companyslug;
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (typeof s !== 'string' || s.trim() === '') return null;
  return normalizeCompanySlugFromRouteParam(s);
}

type MinimalPageContext = {
  routeParams?: Record<string, unknown>;
  urlPathname?: string;
};

/**
 * Resolves the company profile slug for +data and client hooks.
 * Prefer Vike `routeParams.companyslug`; on client navigation it is sometimes empty — then derive from the URL path.
 */
export function resolveCompanyProfileRouteSlug(
  pageContext: MinimalPageContext | null | undefined,
): string | null {
  const fromParams = routeParamCompanyslug(pageContext?.routeParams);
  if (fromParams) return fromParams;

  const fromContext = pageContext?.urlPathname;
  const pathname =
    typeof fromContext === 'string' && fromContext.trim() !== ''
      ? fromContext
      : typeof window !== 'undefined'
        ? window.location.pathname
        : '';

  return extractCompanyProfileSlugFromUrlPathname(String(pathname));
}
